import {useEffect, useRef, useState} from 'react'

import {AlertFillIcon, CheckCircleFillIcon} from '@primer/octicons-react'
import {CTAForm, FormControl, TextInput} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import * as z from 'zod'

import {announce} from '@github-ui/aria-live'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import styles from './EmailCaptureForm.module.css'

const EMAIL_SCHEMA = z.email()

/**
 * Moves focus to an outcome message as soon as it is committed. Declared at module scope so the
 * callback ref keeps a stable identity and therefore only runs when the node mounts or unmounts.
 */
function focusOnMount(node: HTMLDivElement | null) {
  node?.focus()
}

/** Lifecycle phase of the form. Exposed so consumers can mirror it in their own instrumentation. */
export type EmailCaptureFormStatus = 'idle' | 'pending' | 'success' | 'error'
export type EmailCaptureFormLabelVariant = 'above' | 'floating'

type EmailCaptureFormSubmission =
  | {
      /** Receives a trimmed, validated email; resolves into success or rejects into error. */
      onSubmit: (email: string) => void | Promise<void>
      action?: never
      hiddenFields?: never
      required?: never
    }
  | {
      /** Uses a native GET submission instead of the asynchronous capture lifecycle. */
      action: string
      hiddenFields?: Record<string, string>
      required?: boolean
      onSubmit?: never
    }

export type EmailCaptureFormProps = EmailCaptureFormSubmission & {
  /**
   * Accessible name for the `form` landmark. Give each form on a page a distinct name.
   * @default t('Email capture form')
   */
  formLabel?: string
  /**
   * Visible label for the email field.
   * @default t('Email address')
   */
  label?: string
  /**
   * Positions the visible label above the field or floats it inside the field.
   * @default 'above'
   */
  labelVariant?: EmailCaptureFormLabelVariant
  /** Keeps the input and button side by side within a shared surface. */
  layout?: 'separate' | 'joined'
  /** Optional supplementary guidance rendered under the field and wired into `aria-describedby`. */
  hint?: string
  /**
   * Placeholder text for the email field.
   * @default t('Enter your email')
   */
  placeholder?: string
  /**
   * Label for the submit button.
   * @default t('Subscribe')
   */
  submitLabel?: string
  /** Serialized Hydro event attached to the submit button. */
  submitAnalyticsEvent?: string
  /**
   * Politely announced while the submission is in flight.
   * @default t('Submitting your email address…')
   */
  pendingMessage?: string
  /**
   * Shown and focused after `onSubmit` resolves.
   * @default t('Thanks! Your email address has been submitted.')
   */
  successMessage?: string
  /**
   * Shown and focused after `onSubmit` rejects.
   * @default t('Something went wrong. Please try again.')
   */
  errorMessage?: string
  /**
   * Field-level message shown when the field is empty on submit.
   * @default t('This field is required.')
   */
  requiredMessage?: string
  /**
   * Field-level message shown when the value is not a valid email address.
   * @default t('Please enter a valid email address.')
   */
  invalidMessage?: string
  /**
   * `name` attribute for the email input.
   * @default 'email'
   */
  inputName?: string
  /**
   * `autocomplete` attribute for the email input.
   * @default 'email'
   */
  autoComplete?: string
  /** Additional class name applied to the root element. */
  className?: string
  /**
   * Reserves space for the error or success message to prevent layout shift.
   * Disable when the surrounding container can grow to fit an outcome message.
   * @default true
   */
  reserveMessageSpace?: boolean
  /**
   * Called once when a valid submission starts. Intentionally receives no arguments so the email
   * address is never exposed through lifecycle instrumentation.
   */
  onPending?: () => void
  /**
   * Called once after `onSubmit` resolves. Intentionally receives no arguments so the email address
   * is never exposed through lifecycle instrumentation.
   */
  onSuccess?: () => void
  /**
   * Called once after `onSubmit` rejects, with the rejection reason only. Intentionally never
   * receives the email address.
   */
  onError?: (error: unknown) => void
  /** Ref to the underlying `form` element. */
  ref?: React.Ref<HTMLFormElement>
}

/**
 * A reusable email capture form built on Primer Brand `CTAForm`, `FormControl`, and `TextInput`.
 *
 * Collects a single email address, validates it on the client, and reports the outcome through
 * accessible status messaging with deliberate focus movement. It deliberately has no checkbox or
 * consent field: pair it with your own consent UI when a campaign requires one.
 */
export function EmailCaptureForm(props: EmailCaptureFormProps) {
  const {
    hint,
    labelVariant = 'above',
    layout = 'separate',
    inputName = 'email',
    autoComplete = 'email',
    className,
    onPending,
    onSuccess,
    onError,
    reserveMessageSpace = true,
    ref,
  } = props

  // Localized microcopy is resolved during render so the detected locale applies, and so a caller
  // can override any visible string.
  const formLabel = props.formLabel ?? t('Email capture form')
  const label = props.label ?? t('Email address')
  const placeholder = props.placeholder ?? t('Enter your email')
  const submitLabel = props.submitLabel ?? t('Subscribe')
  const pendingMessage = props.pendingMessage ?? t('Submitting your email address…')
  const successMessage = props.successMessage ?? t('Thanks! Your email address has been submitted.')
  const errorMessage = props.errorMessage ?? t('Something went wrong. Please try again.')
  const requiredMessage = props.requiredMessage ?? t('This field is required.')
  const invalidMessage = props.invalidMessage ?? t('Please enter a valid email address.')

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<EmailCaptureFormStatus>('idle')
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  // Incremented on every blocked submission so a repeated failure still moves focus.
  const [fieldFocusRequest, setFieldFocusRequest] = useState(0)

  const inputRef = useRef<HTMLInputElement>(null)
  // Guards against a second submit landing before the pending render commits.
  const pendingRef = useRef(false)
  // Identifies the newest submission so a slow, superseded promise cannot resolve over a newer one.
  const submissionRef = useRef(0)

  const isPending = status === 'pending'
  const isFloatingLabel = labelVariant === 'floating'

  // Focus is browser state, so it is synchronized after the render that commits the validation
  // message. Moving focus only once the message, `aria-invalid`, and `aria-describedby` are in the
  // DOM is what lets assistive technology announce the field together with the reason it failed.
  useEffect(() => {
    if (fieldFocusRequest === 0) {
      return
    }

    inputRef.current?.focus()
  }, [fieldFocusRequest])

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
    setValidationMessage(null)
    // Editing invalidates a previous outcome, but never interrupts an in-flight submission.
    setStatus(current => (current === 'pending' ? current : 'idle'))
  }

  function validate(value: string): string | null {
    if (value.length === 0) {
      return requiredMessage
    }

    return EMAIL_SCHEMA.safeParse(value).success ? null : invalidMessage
  }

  async function submit() {
    if (props.action !== undefined) return

    const value = email.trim()
    const failure = validate(value)

    if (failure !== null) {
      setValidationMessage(failure)
      setStatus('idle')
      setFieldFocusRequest(current => current + 1)
      return
    }

    pendingRef.current = true
    submissionRef.current += 1
    const token = submissionRef.current

    setValidationMessage(null)
    setStatus('pending')
    // Announced through the shared live region because the button stays enabled and the pending
    // state has no other non-visual signal.
    announce(pendingMessage)
    onPending?.()

    try {
      await props.onSubmit(value)
    } catch (error) {
      if (token !== submissionRef.current) {
        return
      }

      pendingRef.current = false
      setStatus('error')
      onError?.(error)
      return
    }

    if (token !== submissionRef.current) {
      return
    }

    pendingRef.current = false
    setEmail('')
    setStatus('success')
    onSuccess?.()
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (pendingRef.current) {
      return
    }

    void submit()
  }

  return (
    <div className={clsx(styles['EmailCaptureForm'], className)}>
      <CTAForm
        ref={ref}
        action={props.action}
        method={props.action === undefined ? undefined : 'get'}
        noValidate={props.action === undefined}
        aria-label={formLabel}
        className={clsx(
          styles['EmailCaptureForm__form'],
          layout === 'joined' && styles['EmailCaptureForm__form--joined'],
        )}
        onSubmit={props.action === undefined ? handleSubmit : undefined}
      >
        <CTAForm.Input className={styles.InputGroup}>
          {props.hiddenFields &&
            Object.entries(props.hiddenFields).map(([name, value]) => (
              <input key={name} type="hidden" name={name} value={value} />
            ))}
          <FormControl
            fullWidth
            required={props.required ?? true}
            className={clsx(styles.FormControl, isFloatingLabel && styles.FloatingFormControl)}
            data-has-value={email.length > 0 ? true : undefined}
            validationStatus={validationMessage === null ? undefined : 'error'}
          >
            <FormControl.Label
              showRequiredIndicator={false}
              className={clsx(styles.Label, isFloatingLabel && styles.FloatingLabel)}
            >
              {label}
            </FormControl.Label>

            <TextInput
              ref={inputRef}
              className={clsx(styles.Field, isFloatingLabel && styles.FloatingField)}
              invisible={layout === 'joined'}
              type="email"
              name={inputName}
              value={email}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={validationMessage === null ? undefined : true}
              readOnly={isPending}
              onChange={handleChange}
            />

            {hint === undefined ? null : <FormControl.Hint className={styles.SupportingText}>{hint}</FormControl.Hint>}

            {validationMessage === null ? null : (
              <FormControl.Validation className={styles.SupportingText} validationStatus="error">
                {validationMessage}
              </FormControl.Validation>
            )}
          </FormControl>
        </CTAForm.Input>

        <CTAForm.Action
          variant="primary"
          data-analytics-event={props.submitAnalyticsEvent}
          className={clsx(
            styles.Submit,
            isFloatingLabel && styles.FloatingSubmit,
            isPending && styles['EmailCaptureForm__submitPending'],
          )}
        >
          {submitLabel}
        </CTAForm.Action>
      </CTAForm>

      {reserveMessageSpace || status === 'error' || status === 'success' ? (
        <div className={styles['EmailCaptureForm__messages']} data-testid="email-capture-form-messages">
          {status === 'error' ? (
            <div
              ref={focusOnMount}
              tabIndex={-1}
              className={clsx(styles['EmailCaptureForm__message'], styles['isError'])}
            >
              <AlertFillIcon className={styles['EmailCaptureForm__messageIcon']} />
              {errorMessage}
            </div>
          ) : null}

          {status === 'success' ? (
            <div
              ref={focusOnMount}
              tabIndex={-1}
              className={clsx(styles['EmailCaptureForm__message'], styles['isSuccess'])}
            >
              <CheckCircleFillIcon className={styles['EmailCaptureForm__messageIcon']} />
              {successMessage}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
